import { ArrowUpRight } from 'lucide-react';
import { Button } from './ui/button';
import { useSite } from '../lib/SiteContentContext';
import { BOOK_TRIAL_URL } from '../lib/config';
import { imageAttrs } from '../lib/imageUrl';

// Two-column grid at lg+, single column below.
const HOW_IMAGE_SIZES = '(min-width: 1024px) 50vw, 100vw';

export default function HowItWorks() {
  const { howItWorks, CTA, siteSettings } = useSite();
  const rawImage = siteSettings?.howItWorksImage || '/hero/how-it-works.webp';
  const imgProps = imageAttrs(rawImage, { sizes: HOW_IMAGE_SIZES, fallbackWidth: 800 });
  return (
    <section className="py-14 md:py-20 container" aria-labelledby="how-heading">
      {/* Heading (scrolls normally) */}
      <div className="max-w-2xl">
        <div className="text-brand-coral text-sm font-semibold uppercase tracking-widest">How it works</div>
        <h2 id="how-heading" className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl text-brand-ink">
          Four simple steps to <span className="italic text-brand-teal">get started</span>.
        </h2>
        <p className="mt-3 text-brand-ink/70 text-base md:text-lg max-w-lg">
          From your free trial lesson to regular progress updates, we make joining Science Sorted simple.
        </p>
      </div>

      {/* Two-column layout - image sticks on the left while steps scroll on the right */}
      <div className="mt-8 md:mt-10 grid lg:grid-cols-2 gap-8 lg:gap-14 items-start">
        <div className="lg:sticky lg:top-28">
          <div className="rounded-3xl overflow-hidden aspect-[4/5] lg:aspect-[4/3]">
            <img
              {...imgProps}
              alt="A tidy study desk with a Google Classroom lesson on Advanced Genetics open on the laptop, a revision timetable pinned above and A Level Biology revision notes in front"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div>
          <ol className="space-y-4 md:space-y-5">
            {howItWorks.map((s) => (
              <li
                key={s.step}
                className="group rounded-3xl border border-brand-ink/10 bg-white p-6 md:p-8 hover:bg-brand-butter/40 transition-colors"
              >
                <div className="flex items-baseline gap-4 md:gap-6">
                  <div
                    className="font-display text-5xl md:text-6xl text-brand-sun/70 group-hover:text-brand-sun transition-colors shrink-0"
                    aria-hidden="true"
                  >
                    {s.step}
                  </div>
                  <div>
                    <h3 className="font-display text-xl md:text-2xl text-brand-ink">
                      {s.title}
                    </h3>
                    <p className="mt-1.5 text-brand-ink/75 leading-relaxed">{s.body}</p>
                  </div>
                </div>
              </li>
            ))}
          </ol>

          {/* Yellow CTA under step four */}
          <div className="mt-6 md:mt-8">
            <Button
              asChild
              size="lg"
              className="bg-brand-sun text-brand-ink hover:bg-brand-ink hover:text-brand-sun rounded-full h-12 px-6 font-semibold shadow-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-ink"
            >
              <a
                href={BOOK_TRIAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="gap-2"
              >
                {CTA.primary} <ArrowUpRight className="w-4 h-4" />
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
